/**
 * Trade event → user-row trading-streak advancer.
 *
 * The market-scoped wallet trade event carries
 * `tradeCountDelta` (`1` for a real trade; `0` for the backing/boost/claim
 * variants that reuse the same proto). A streak only advances on the FIRST
 * qualifying trade of a NEW local day, so — unlike a money `add:` — we can't
 * blindly increment. This helper checks the cached `streakLastLocalDate`
 * before bumping, then re-derives the tier level.
 *
 * The day boundary here uses the BROWSER timezone (close enough for an
 * optimistic chip bump); the server's recompute-on-read — keyed to the user's
 * country timezone — is the source of truth and self-heals any drift on the
 * next fetch.
 *
 * Immutable: returns the exact `prev` reference when nothing changes (the
 * common same-day-trade case) so unrelated leaves keep structural-sharing
 * identity and don't re-render. Platform-agnostic — pure data.
 */

/** Tier minDays for levels 1..7 (Spark…Eternal). Mirrors @morfi/backend-shared STREAK_TIERS. */
const TIER_MIN_DAYS = [1, 3, 7, 14, 30, 90, 180] as const;

function levelOf(days: number): number {
  let level = 0;
  TIER_MIN_DAYS.forEach((minDays, i) => {
    if (days >= minDays) level = i + 1;
  });
  return level;
}

function coerceDelta(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === 'bigint') return Number.isFinite(Number(v)) ? Number(v) : 0;
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return Number(v.trim());
  return 0;
}

/** 'YYYY-MM-DD' for a Date in the browser's local timezone. */
function localDateString(d: Date): string {
  return new Intl.DateTimeFormat('en-CA').format(d);
}

/** Add `n` calendar days to a 'YYYY-MM-DD' string (UTC math; DST-safe for dates). */
function addDays(isoDate: string, n: number): string {
  const [ys = '0', ms = '1', ds = '1'] = isoDate.split('-');
  const dt = new Date(Date.UTC(Number(ys), Number(ms) - 1, Number(ds)));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

export function advanceStreakOnTrade<
  R extends {
    currentStreak?: number;
    streakLevel?: number;
    streakLastLocalDate?: string | null;
  },
>(prev: R, raw: { tradeCountDelta?: number | bigint | string }): R {
  // Only a real trade advances the streak; backing/boost variants carry delta 0.
  if (coerceDelta(raw.tradeCountDelta) < 1) return prev;

  const today = localDateString(new Date());
  const last = prev.streakLastLocalDate ?? null;
  if (last === today) return prev; // already traded today — no change

  const current =
    typeof prev.currentStreak === 'number' && Number.isFinite(prev.currentStreak)
      ? prev.currentStreak
      : 0;
  // Consecutive day → extend; gap or first-ever → restart at 1.
  const next = last !== null && addDays(last, 1) === today ? current + 1 : 1;

  return { ...prev, currentStreak: next, streakLevel: levelOf(next), streakLastLocalDate: today };
}
